import React, { useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { CellContext, ColumnDef } from '@tanstack/react-table';
import useCheckoutDesign from 'api/useCheckoutDesign';
import { EStudioPageTitle } from 'design-system/AcSidebar/pages';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { usePageTypeByFeatureFlag } from 'hooks/usePageTypeByFF';
import { usePaginationFromURL } from 'hooks/usePaginationFromURL';
import { useUserPermissions } from 'hooks/useUserPermissions';
import { EToasterStatus, showToast } from 'utils/showToast';

import ActionCell from '../../components/DataTableComponent/components/ActionCell';
import DataTable from '../../components/DataTableComponent/DataTable';
import PortalHeader from '../../components/PortalHeader/PortalHeader';
import { renderActiveStatusChip } from '../../utils/statusChipUtils';

import { CheckoutDesign } from './types';

const CheckoutDesignTable = (): React.ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const { publisherId } = useParams();
  const { page, rows } = usePaginationFromURL('offers');

  const { getCheckoutDesigns, deleteCheckoutDesign, updateCheckoutDesign } =
    useCheckoutDesign(publisherId);

  const [selectedCheckoutDesign, setSelectedCheckoutDesign] = useState<
    string | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(page);

  const { isPageDisabled } = usePageTypeByFeatureFlag('Checkout Design');
  const { isActionEnabled: checkActionEnabled } = useUserPermissions();
  const isEmpty =
    getCheckoutDesigns?.data && getCheckoutDesigns?.data?.length === 0;
  const totalCount = getCheckoutDesigns.data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / rows);

  useEffect(() => {
    if (!selectedCheckoutDesign) return;
    deleteCheckoutDesign.mutate(selectedCheckoutDesign, {
      onSuccess: () => {
        showToast({
          message: 'Checkout design deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        getCheckoutDesigns.refetch();
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete checkout design',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
      },
      onSettled: () => {
        setSelectedCheckoutDesign(null);
      }
    });
  }, [selectedCheckoutDesign]);

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set('page', String(currentPage));
    queryParams.set('rows', String(rows));

    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true
    });
    getCheckoutDesigns.refetch();
  }, [currentPage, rows]);

  useEffect(() => {
    setIsLoading(
      getCheckoutDesigns.isFetching ||
        getCheckoutDesigns.isRefetching ||
        getCheckoutDesigns.isLoading
    );
  }, [getCheckoutDesigns.data, getCheckoutDesigns.isRefetching]);

  const isManageEnabled = useMemo(
    () =>
      checkActionEnabled(
        null,
        EPermissionGroup.BUILDER,
        EPermissionAction.MANAGE,
        publisherId
      ),
    [publisherId, checkActionEnabled]
  );

  const handleToggleActiveStatus = async (
    templateId: string,
    active: boolean
  ): Promise<void> => {
    updateCheckoutDesign.mutate(
      { form: { isActive: !active }, templateId },
      {
        onSuccess: () => {
          showToast({
            message: active
              ? 'Checkout design deactivated successfully'
              : 'Checkout design activated successfully',
            status: EToasterStatus.SUCCESS
          });
          getCheckoutDesigns.refetch();
        },
        onError: () => {
          showToast({
            message: 'Error editing checkout design',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  };

  const columns: ColumnDef<CheckoutDesign, unknown>[] = [
    {
      accessorKey: 'externalTemplateId',
      header: 'Checkout External ID',
      cell: (cell: CellContext<CheckoutDesign, unknown>) => cell.getValue()
    },
    {
      accessorKey: 'name',
      header: 'Checkout Name',
      cell: (cell: CellContext<CheckoutDesign, unknown>) => cell.getValue()
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      accessorFn: (row: CheckoutDesign) => {
        return row.isActive ? 'Active' : 'Inactive';
      },
      cell: (cell: CellContext<CheckoutDesign, unknown>) => {
        return renderActiveStatusChip(cell.row.original.isActive);
      }
    },
    {
      accessorKey: 'actions',
      header: '',
      size: 60,
      cell: (cell: CellContext<CheckoutDesign, unknown>) => {
        const { original } = cell.row;
        return (
          <div className="max-w-[60px] w-[60px]">
            <ActionCell
              rowData={original}
              actions={[
                {
                  label: original.isActive ? 'Deactivate' : 'Activate',
                  handler: () =>
                    handleToggleActiveStatus(
                      original.templateId || '',
                      original.isActive
                    ),
                  disabled: !isManageEnabled
                },
                {
                  label: 'Edit',
                  handler: () => navigate(`./form/${original.templateId}`),
                  disabled: !isManageEnabled
                },
                {
                  label: 'Duplicate',
                  handler: () => navigate(`./dup/${original.templateId}`),
                  disabled: !isManageEnabled
                },
                {
                  label: 'Delete',
                  handler: () => {
                    setSelectedCheckoutDesign(original.templateId || null);
                  },
                  disabled: !isManageEnabled
                }
              ]}
            />
          </div>
        );
      }
    }
  ];

  return (
    <div
      className={`${isPageDisabled ? 'opacity-50 pointer-events-none' : 'opacity-100 pointer-events-auto'}`}
    >
      <PortalHeader
        title={EStudioPageTitle.CHECKOUT_DESIGN}
        primaryButtonText="Create New Checkout Design"
        onPrimaryButtonClick={() => navigate('./form')}
        isCreateButton
      />
      <div className="px-7 pb-9 pt-2.5">
        <DataTable
          data={getCheckoutDesigns?.data}
          columns={columns}
          rowIdIdentifier="externalTemplateId"
          isLoading={isLoading}
          rowCount={totalCount}
          rowsPerPage={rows}
          showPagination={totalPages > 1}
          manualPagination
          pageIndex={currentPage}
          pageCount={totalPages}
          onPageChange={setCurrentPage}
          emptyStateConfig={{
            title: 'Checkout design',
            isEmpty,
            description: (
              <>
                Create and customize your checkout screen to personalize the
                payment <br />
                experience for your players.
              </>
            ),
            onClick: () => navigate('./form')
          }}
          bordered
        />
      </div>
    </div>
  );
};

export { CheckoutDesignTable };
